import React, { useEffect, useState } from 'react';

import clientServerBridge from '@helpcenter/client-server-bridge';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { operationalEvent } from '@atlassian/help-center-common-util/analytics/events';
import type { CmdbObject } from '../../rest/cmdb-object-picker';
import { MemoizedInsightIcon } from '../cmdb-object-picker/insight-icon';
import { AttributeList } from './attribute-list';
import { ExpandCollapseToggle } from './expand-collapse-toggle';
import messages from './messages';

export interface Props {
    cmdbObject: CmdbObject | null;
}

const CmdbObjectCard = ({ intl, cmdbObject }: Props & WrappedComponentProps) => {
    const [expanded, setExpanded] = useState(false);

    const timezoneId =
        // Upgrading @atlaskit/platform; fix these violations.
        // eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
        typeof document !== 'undefined'
            ? clientServerBridge.querySelector('meta[name="user-timezone"]')?.getAttribute('content')
            : undefined;

    useEffect(() => {
        // Fire an event when the component is rendered, to measure successful renders
        if (cmdbObject !== null) {
            operationalEvent({
                action: 'rendered',
                actionSubject: 'cmdbObjectCard',
                source: 'cmdbObjectCard',
            });
        }
    }, [cmdbObject]);

    if (cmdbObject === null) {
        return intl.formatMessage(messages.error);
    }

    const renderChevronButton = () => {
        if (cmdbObject.attributes.length === 0) {
            return null;
        }
        const buttonLabel = intl.formatMessage(expanded ? messages.collapseLabel : messages.expandLabel);
        return (
            <ExpandCollapseToggle
                isExpanded={expanded}
                label={buttonLabel}
                onClick={() => setExpanded(!expanded)}
                testId="cmdb-object-card-expand-collapse-toggle"
            />
        );
    };

    return (
        <Box xcss={containerStyles} testId="cmdb-object-card">
            <Inline alignBlock="center">
                <MemoizedInsightIcon {...cmdbObject.avatar.mediaClientConfig} />
                <Box xcss={labelStyles}>{cmdbObject.label}</Box>
                {renderChevronButton()}
            </Inline>
            {expanded && (
                <Box xcss={attributesListContainerStyles}>
                    <AttributeList attributes={cmdbObject.attributes} timezoneId={timezoneId} />
                </Box>
            )}
        </Box>
    );
};
export default injectIntl(CmdbObjectCard);

const containerStyles = xcss({
    position: 'relative',
    width: '100%',
    maxWidth: '600px',
    borderWidth: 'border.width',
    borderStyle: 'solid',
    borderColor: 'color.border',
    borderRadius: 'radius.small',
    marginBottom: 'space.100',
    padding: 'space.100',
    boxSizing: 'border-box',
});

const labelStyles = xcss({
    font: 'font.heading.xsmall',
    color: 'color.text.subtle',
    margin: 'space.0',
    paddingLeft: 'space.100',
    flexGrow: 1,
    minWidth: 'space.0',
    overflowWrap: 'break-word',
});

const attributesListContainerStyles = xcss({
    marginTop: 'space.200',
});
